// Adapted from eval/dashboard/internal_web/src/components/session/DifferentialParts.tsx (ATM-57): live gap and comparison copy. Keep in step until ATM-60 shares it.
/**
 * The per-answer differential panel's parts around its chart — the eval dashboard's
 * `DifferentialParts`: the header and its stepper, the comparison line, and the states where
 * there is nothing to rank. Each is exactly one line or one block, so the fixed-height panel
 * never changes shape from one answer to the next.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/cn";
import { count } from "../../lib/format";
import Button from "../ui/Button";
import InfoTip from "../ui/InfoTip";
import NullNote from "../ui/NullNote";
import { beliefCaption, type ExchangeBelief } from "./liveBeliefs";

/** Step to the previous / next answer; absent where there is none, which disables the arrow. */
export type StepProps = {
  onPrev?: () => void;
  onNext?: () => void;
};

/**
 * The tabs and the stepper, on one line; the stepper never moves, so repeated clicks land.
 * Which answer is shown is the transcript's anchored exchange, so it is not printed again here —
 * the stepper carries it as its hover text and its accessible name.
 */
export function PanelHeader({
  belief,
  active,
  heading,
  onPrev,
  onNext,
}: StepProps & { belief: ExchangeBelief; active: boolean; heading: ReactNode }) {
  const caption = beliefCaption(belief, active);
  const steps = [
    { label: "The previous answer", Icon: ChevronLeft, onClick: onPrev },
    { label: "The next answer", Icon: ChevronRight, onClick: onNext },
  ];
  return (
    <header className="flex items-center gap-3">
      <div className="min-w-0 shrink">{heading}</div>
      <div
        className="ml-auto flex shrink-0 items-center gap-1.5"
        role="group"
        aria-label={`Step — ${caption}`}
        title={caption}
      >
        {steps.map(({ label, Icon, onClick }) => (
          <Button
            key={label}
            variant="outline"
            size="sm"
            className="px-2.5"
            aria-label={label}
            disabled={onClick === undefined}
            onClick={onClick}
          >
            <Icon size={14} aria-hidden />
          </Button>
        ))}
      </div>
    </header>
  );
}

/** Why there is nothing to rank here — each gap said as the different finding it is. */
export function BeliefGap({ belief, active }: { belief: ExchangeBelief; active: boolean }) {
  if (belief.answerNumber === null) {
    return (
      <NullNote>
        {active
          ? "Waiting for Jiva's first differential — it forms once the patient has described what brought them in."
          : "No answer in this session stored a differential."}
      </NullNote>
    );
  }
  if (belief.differential !== null) {
    return <NullNote>Jiva processed this answer and its graph ranked nothing at all.</NullNote>;
  }
  return (
    <p className="flex items-start gap-1.5 text-xs text-ink-muted">
      <span>No differential was stored for this answer.</span>
      <InfoTip label="Why an answer carries no differential" align="end">
        <p>
          The backend stores med-reasoning&rsquo;s ranked differential on each answer once the
          answer has been processed.
        </p>
        <p>
          None was stored here: the call that carries it failed, or the session predates
          per-answer differentials.
        </p>
      </InfoTip>
    </p>
  );
}

/** What the changes are measured against, in one line — always one, so no step is shorter. */
export function CompareNote({ belief, className }: { belief: ExchangeBelief; className?: string }) {
  if (belief.previous === null || belief.previousAnswerNumber === null) {
    return (
      <p className={cn("truncate text-caption text-ink-faint", className)}>
        Jiva&rsquo;s first belief: nothing before it to compare with.
      </p>
    );
  }
  return (
    <p className={cn("flex min-w-0 items-center gap-1.5 text-caption text-ink-faint", className)}>
      <span className="truncate">
        Changes are against the belief after answer {count(belief.previousAnswerNumber)}
      </span>
      <InfoTip label="What the change measures" align="end">
        <p>
          Each entry&rsquo;s rank and likelihood against the belief after the previous answer —
          what this answer did to the differential.
        </p>
        <p>
          A question med-reasoning answered itself in between updates the graph too, so its effect
          is folded into this answer&rsquo;s change. Neither direction is a score: a disease falling
          can be exactly what a good answer does.
        </p>
      </InfoTip>
    </p>
  );
}
